import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import set from 'lodash/set';
import isMatchWith from 'lodash/isMatchWith';
import every from 'lodash/every';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import { differenceBy, unset } from 'lodash';
import pick from 'lodash/pick';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { checkTransactions } from '../../actions/bundles';
import StackGrid from '../StackGrid';
import DatePicker from '../DatePicker';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import TextButton from '../TextButton';
import SubModuleContent from '../SubModuleContent';
import BundledProductList from '../BundledProductList';
import styles from './ReviseBundleForm.style';

const BundleSchema = Yup.object().shape({
  startDate: Yup.date().required('Required'),
  endDate: Yup.date().required('Required'),
  componentAllocationPercentage: Yup.array(
    Yup.object()
      .shape({
        allocationPercentage: Yup.number().min(0).max(100),
        isRoyaltyBearing: Yup.boolean().required('Required'),
        productId: Yup.string().required(),
      })
      .required('Required'),
  ).required('Required'),
});

const ReviseBundleForm = React.forwardRef(
  ({ bundleVersion, onUpdate, onSuccess, handleShowDialog, onReviseSave }, ref) => {
    const { reviseBundleFormData } = useLocation();
    const { bundleId } = useParams();
    const dispatch = useDispatch();

    const [initialValues, setinitialValues] = useState({
      startDate: bundleVersion.startDate || '',
      endDate: bundleVersion.endDate || '',
      componentAllocationPercentage: bundleVersion.componentAllocationPercentage || [],
    });
    const [versionData, setVersionData] = useState([]);
    const [products, setProducts] = useState([]);
    const [allProductsSelected, setAllProductsSelected] = useState(false);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [showAllocationError, setShowAllocationError] = useState(false);
    const [isAllocationError, setIsAllocationError] = useState('');
    const [errorFields, setErrorFields] = useState(null);

    const getKeyValueProducts = productData => {
      const productsForBundle = productData.map(product => {
        let item = product;
        if (!item.productsIds) {
          item = {
            name: product?.productName || product?.name,
            tokenId: product?.tokenId,
            isRoyaltyBearing:
              !isNull(product?.isRoyaltyBearing) && !isUndefined(product?.isRoyaltyBearing)
                ? product?.isRoyaltyBearing
                : true,
            allocationPercentage: product?.allocationPercentage || 0,
            productsIds: product?.productsIds || [],
          };
          if (product?.allProductIds) {
            item.productsIds = product.allProductIds.map(id => {
              return {
                key: id,
                text: id,
              };
            });
          } else {
            item.productsIds = product?.metadata?.productIds.map(id => {
              return {
                key: id,
                text: id,
              };
            });
          }
          item.productId = product?.productId || item.productsIds[0].key;
        }
        return item;
      });
      return productsForBundle;
    };

    useEffect(() => {
      if (reviseBundleFormData) {
        const revisedBundleData = cloneDeep(reviseBundleFormData);
        if (Array.isArray(reviseBundleFormData.componentAllocationPercentage)) {
          const productsForBundle = getKeyValueProducts(
            reviseBundleFormData.componentAllocationPercentage,
          );
          set(revisedBundleData, 'componentAllocationPercentage', productsForBundle);
          setProducts(productsForBundle);
        }
        const bundleVersionInitial = cloneDeep(bundleVersion);
        if (Array.isArray(bundleVersionInitial.componentAllocationPercentage)) {
          const productsForBundle = getKeyValueProducts(
            bundleVersionInitial.componentAllocationPercentage,
          );
          set(bundleVersionInitial, 'componentAllocationPercentage', productsForBundle);
        }
        setinitialValues(revisedBundleData);
        setVersionData(bundleVersionInitial);
        setErrorFields(reviseBundleFormData.errorFields);
      }
    }, [reviseBundleFormData]);

    useEffect(() => {
      if (bundleVersion && !versionData?.length && !reviseBundleFormData) {
        const bundleVersionInitial = cloneDeep(bundleVersion);
        if (Array.isArray(bundleVersionInitial.componentAllocationPercentage)) {
          const productsForBundle = getKeyValueProducts(
            bundleVersionInitial.componentAllocationPercentage,
          );
          set(bundleVersionInitial, 'componentAllocationPercentage', productsForBundle);
          setProducts(productsForBundle);
        }
        setinitialValues(bundleVersionInitial);
        setVersionData(bundleVersionInitial);
      }
    }, [bundleVersion]);

    const handleSelectAllButton = () => {
      const allProducts = products.map(item => item.tokenId);
      setSelectedProducts(allProducts);
      setAllProductsSelected(true);
    };

    const handleDeselectAllButton = () => {
      setSelectedProducts([]);
      setAllProductsSelected(false);
    };

    const handleProductSelect = prods => {
      setSelectedProducts(prods);
    };

    const handleAllocationChange = (showError, hasError) => {
      setShowAllocationError(showError);
      setIsAllocationError(hasError);
    };

    const checkValueChanged = (updatedValue, oldValue, key) => {
      if (key === 'startDate' || key === 'endDate') {
        return Date.parse(updatedValue) === Date.parse(oldValue);
      }
      if (key === 'componentAllocationPercentage') {
        const allocations = oldValue.map(item => {
          const newAllocation = find(updatedValue, ['tokenId', item.tokenId]);
          if (newAllocation) {
            return (
              item.isRoyaltyBearing === newAllocation.isRoyaltyBearing &&
              item.productId === newAllocation.productId &&
              item.allocationPercentage?.toString() ===
                newAllocation.allocationPercentage?.toString()
            );
          }

          return false;
        });
        const newProducts = differenceBy(updatedValue, oldValue, 'tokenId');

        return every(allocations, item => item) && !newProducts?.length;
      }

      return true;
    };

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize
        validationSchema={BundleSchema}
        onSubmit={async values => {
          let result;
          if (!showAllocationError && isAllocationError) {
            setShowAllocationError(true);
          } else if (!showAllocationError) {
            const submittedValues = omit(values, 'errorFields');
            submittedValues.componentAllocationPercentage =
              submittedValues?.componentAllocationPercentage.map(prod => {
                const item = prod;
                item.allocationPercentage = prod.allocationPercentage || 0;
                return item;
              });
            // checking revision definition does match current definition
            const isValuesEqual = isMatchWith(submittedValues, versionData, checkValueChanged);
            if (bundleId && !isValuesEqual) {
              result = await onReviseSave();
            } else {
              onSuccess();
            }
          }

          return result;
        }}
      >
        {({ isSubmitting, errors, setFieldValue, values, setErrors }) => {
          const handleRemoveProduct = (productsToRemove, type) => {
            const updatedProducts = products.filter(
              product => !productsToRemove.includes(product.tokenId),
            );
            setFieldValue('componentAllocationPercentage', updatedProducts);
            setProducts(updatedProducts);
            if (type === 'removeSelected') {
              setSelectedProducts([]);
              setAllProductsSelected(false);
            }
            onUpdate(updatedProducts?.length > 0);
          };
          const handleUpdatedProductList = (updatedProducts, type) => {
            // update productlist in the bundle
            setFieldValue('componentAllocationPercentage', updatedProducts);
            if (type === 'allocationValue') {
              setProducts(updatedProducts);
            }
          };

          // checking date change will result in orphaning transactions if so show error screen
          const handleDateFieldChange = async (name, selectedDate) => {
            let result;
            const updatedErrors = omit(errors, name);
            const newDate = new Date(selectedDate);
            const oldDate = new Date(versionData?.[name]);
            const expirationError = 'Expiration Date must be later than Effective Start Date';
            if (name === 'endDate' && newDate <= new Date(values?.startDate)) {
              setErrors({
                ...errors,
                [name]: expirationError,
              });
              set(updatedErrors, [name], expirationError);
            } else if (name === 'startDate' && newDate >= new Date(values?.endDate)) {
              setErrors({
                ...errors,
                endDate: expirationError,
              });
              set(updatedErrors, 'endDate', expirationError);
            } else if (
              (name === 'startDate' && newDate > oldDate) ||
              (name === 'endDate' && (newDate < oldDate || newDate < new Date()))
            ) {
              const tempDate = new Date(selectedDate).toISOString();
              result = await dispatch(
                checkTransactions(bundleId, versionData?.[name], tempDate, name),
              );
              if (result?.payload) {
                handleShowDialog('orphaned');
                setErrors({ ...errors, [name]: 'Date range has existing transactions' });
              } else {
                setErrors(updatedErrors);
              }
            } else if (
              name === 'startDate' &&
              newDate < new Date(values?.endDate) &&
              updatedErrors.endDate &&
              updatedErrors.endDate === expirationError
            ) {
              unset(updatedErrors, 'endDate');
              setErrors(updatedErrors);
            } else {
              setErrors(updatedErrors);
            }
            const fieldName = result?.payload && name;
            const dateFieldErrors = pick(updatedErrors, ['startDate', 'endDate']);
            if (fieldName || !isEmpty(dateFieldErrors)) {
              const invalidFields = fieldName
                ? dateFieldErrors.concat({ fieldName: 'Date range has existing transactions' })
                : dateFieldErrors;
              setFieldValue('errorFields', invalidFields);
            } else {
              setFieldValue('errorFields', null);
            }
            onUpdate(isEmpty(dateFieldErrors) && !result?.payload);
          };

          useEffect(() => {
            if (errorFields) {
              setErrors(errorFields);
              onUpdate(isEmpty(errorFields));
            }
          }, [errorFields]);

          return (
            <Form noValidate>
              {errors.form && (
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline
                  dismissButtonAriaLabel="Close"
                  nowrap
                >
                  {errors.form}
                </MessageBar>
              )}
              <SubModule disableAccordian>
                <SubModuleContent className={styles.dateContent}>
                  <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                    <Field
                      name="startDate"
                      label="Effective Start Date"
                      component={DatePicker}
                      className={styles.datePicker}
                      onChange={handleDateFieldChange}
                      required
                      disabled={isSubmitting}
                      allowTextInput
                    />
                    <Field
                      name="endDate"
                      label="Expiration Date"
                      component={DatePicker}
                      className={styles.datePicker}
                      onChange={handleDateFieldChange}
                      required
                      disabled={isSubmitting}
                      allowTextInput
                    />
                  </StackGrid>
                </SubModuleContent>
              </SubModule>

              <SubModule disableAccordian>
                <SubModuleHeader className={styles.moduleHeader}>
                  Bundled Products
                  {products.length > 0 && (
                    <>
                      {selectedProducts.length === 0 ? (
                        <TextButton
                          className={styles.selectButtonDefault}
                          data-testid="select-all"
                          onClick={() => handleSelectAllButton()}
                          data-is-focusable
                        >
                          {' '}
                          Select All
                        </TextButton>
                      ) : (
                        <TextButton
                          className={styles.selectButtonSelected}
                          data-testid="deselect-all"
                          onClick={() => handleDeselectAllButton()}
                          data-is-focusable
                        >
                          {' '}
                          Deselect All
                        </TextButton>
                      )}
                      <TextButton
                        onClick={() => handleRemoveProduct(selectedProducts, 'removeSelected')}
                        disabled={selectedProducts.length === 0}
                        data-is-focusable
                      >
                        Remove Selected
                      </TextButton>
                    </>
                  )}
                </SubModuleHeader>
                <SubModuleContent className={styles.moduleContent}>
                  {products.length > 0 && (
                    <div className={styles.focusableGrid} data-is-focusable>
                      <BundledProductList
                        items={products}
                        onRemove={handleRemoveProduct}
                        handleUpdatedProductList={handleUpdatedProductList}
                        disabled={isSubmitting}
                        className={styles.bundledProducts}
                        enableView
                        disableRowLink
                        showAllocation
                        allProductsSelected={allProductsSelected}
                        handleProductSelect={handleProductSelect}
                        showAllocationError={showAllocationError}
                        setShowAllocationError={handleAllocationChange}
                        selectedProductAllocations={selectedProducts}
                      />
                    </div>
                  )}
                  <Link
                    to={{
                      pathname: '/bundle',
                      bundleFormData: values,
                      bundleId,
                      isEdit: true,
                    }}
                  >
                    <TextButton>Add Products</TextButton>
                  </Link>
                </SubModuleContent>
              </SubModule>
            </Form>
          );
        }}
      </Formik>
    );
  },
);

ReviseBundleForm.propTypes = {
  bundleVersion: PropTypes.object,
  onUpdate: PropTypes.func,
  onSuccess: PropTypes.func,
  handleShowDialog: PropTypes.func,
  onReviseSave: PropTypes.func,
};

ReviseBundleForm.defaultProps = {
  bundleVersion: {},
  onUpdate: () => {},
  onSuccess: () => {},
  handleShowDialog: () => {},
  onReviseSave: () => {},
};

export default ReviseBundleForm;
